import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SentryTest } from "../components/debug/SentryTest";
import { SentryDebugTest } from "../components/debug/SentryDebugTest";
import { Bug, Monitor, Database, Settings, Activity, Info } from "lucide-react";

export const DevTools: React.FC = () => {
  const [systemInfo, setSystemInfo] = useState<{
    userAgent: string;
    platform: string;
    language: string;
    screenResolution: string;
    colorDepth: number;
    timezone: string;
    timestamp: string;
    cookieEnabled: boolean;
    onLine: boolean;
  } | null>(null);

  const getSystemInfo = () => {
    const info = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenResolution: `${screen.width}x${screen.height}`,
      colorDepth: screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
    };
    setSystemInfo(info);
  };

  const clearLocalStorage = () => {
    localStorage.clear();
    alert("Local storage cleared!");
  };

  const clearSessionStorage = () => {
    sessionStorage.clear();
    alert("Session storage cleared!");
  };

  const testConsoleLog = () => {
    console.log("Test log message");
    console.warn("Test warning message");
    console.error("Test error message");
    console.info("Test info message");
  };

  const testNetworkRequest = async () => {
    try {
      const response = await fetch("/api/health", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Network test response:", response.status);
    } catch (error) {
      console.error("Network test failed:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Developer Tools</h1>
        <p className="text-muted-foreground">
          Debugging and development utilities for testing and monitoring.
        </p>
      </div>

      {/* Development Mode Warning */}
      {import.meta.env.DEV && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            You are in development mode. These tools are only available in
            development.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="sentry" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sentry">Sentry</TabsTrigger>
          <TabsTrigger value="system">System Info</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
          <TabsTrigger value="network">Network</TabsTrigger>
          <TabsTrigger value="console">Console</TabsTrigger>
        </TabsList>

        {/* Sentry Tab */}
        <TabsContent value="sentry" className="space-y-4">
          <SentryDebugTest />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="h-5 w-5" />
                Basic Sentry Test
              </CardTitle>
              <CardDescription>
                Simple Sentry error and message testing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SentryTest />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Error Boundary Test</CardTitle>
              <CardDescription>
                Test React error boundary functionality
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  throw new Error("Test error for error boundary");
                }}
                variant="destructive"
              >
                Trigger Error Boundary
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Info Tab */}
        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                System Information
              </CardTitle>
              <CardDescription>
                Browser and system information for debugging
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={getSystemInfo}>Get System Information</Button>

              {systemInfo && (
                <div className="space-y-2">
                  <h4 className="font-medium">System Details:</h4>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">User Agent:</span>
                      <span className="font-mono text-xs">
                        {systemInfo.userAgent}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Platform:</span>
                      <span>{systemInfo.platform}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Language:</span>
                      <span>{systemInfo.language}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Cookies Enabled:
                      </span>
                      <Badge
                        variant={
                          systemInfo.cookieEnabled ? "default" : "destructive"
                        }
                      >
                        {systemInfo.cookieEnabled ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Online:</span>
                      <Badge
                        variant={systemInfo.onLine ? "default" : "destructive"}
                      >
                        {systemInfo.onLine ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Screen Resolution:
                      </span>
                      <span>{systemInfo.screenResolution}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Color Depth:
                      </span>
                      <span>{systemInfo.colorDepth} bits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Timezone:</span>
                      <span>{systemInfo.timezone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Timestamp:</span>
                      <span className="font-mono text-xs">
                        {systemInfo.timestamp}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage Tab */}
        <TabsContent value="storage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Storage Management
              </CardTitle>
              <CardDescription>Manage browser storage and data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-medium">Local Storage</h4>
                  <p className="text-sm text-muted-foreground">
                    Persistent storage that survives browser restarts
                  </p>
                  <Button
                    onClick={clearLocalStorage}
                    variant="destructive"
                    size="sm"
                  >
                    Clear Local Storage
                  </Button>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Session Storage</h4>
                  <p className="text-sm text-muted-foreground">
                    Temporary storage that clears when tab closes
                  </p>
                  <Button
                    onClick={clearSessionStorage}
                    variant="destructive"
                    size="sm"
                  >
                    Clear Session Storage
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Storage Contents</h4>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="text-muted-foreground">
                      Local Storage items:
                    </span>{" "}
                    {localStorage.length}
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">
                      Session Storage items:
                    </span>{" "}
                    {sessionStorage.length}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Network Tab */}
        <TabsContent value="network" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Network Testing
              </CardTitle>
              <CardDescription>
                Test network connectivity and API endpoints
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={testNetworkRequest}>Test Network Request</Button>
              <p className="text-sm text-muted-foreground">
                Check the browser console for network test results.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Console Tab */}
        <TabsContent value="console" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Console Testing
              </CardTitle>
              <CardDescription>
                Test console logging and debugging features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={testConsoleLog}>Test Console Logging</Button>
              <p className="text-sm text-muted-foreground">
                Check the browser console for test log messages.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common debugging tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-3">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
            <Button variant="outline" onClick={() => console.clear()}>
              Clear Console
            </Button>
            <Button variant="outline" onClick={() => window.open("", "_blank")}>
              Open New Tab
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
